import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'reactstrap';
import Navbar from './Navbar';
import Footer from './Footer';
import GuestChatWidget from './GuestChatWidget';
import './landing.scss';
import SeoHead from '../../../Components/Common/SeoHead';
import HelpBreadcrumbSchema from '../../../Components/Common/HelpBreadcrumbSchema';
import HelpScreenshot from './HelpScreenshot';
import type { HelpImage } from './HelpScreenshot';

const HelpArticleBulkImport: React.FC = () => {
  const BASE = '/images/help/bulk-import';

  const screenshots: Record<string, HelpImage[]> = {
    step1: [
      { src: `${BASE}/bi-step-1.png`, alt: 'CSV file format for bulk import', caption: 'Step 1 – Prepare a CSV file with one ASIN per row' },
    ],
    step2: [
      { src: `${BASE}/bi-step-2.png`, alt: 'Bulk import upload interface', caption: 'Step 2 – Upload your CSV and start the import' },
    ],
    step3: [
      { src: `${BASE}/bi-step-3.png`, alt: 'Import progress monitoring screen', caption: 'Step 3 – Monitor import progress in real time' },
    ],
    step4: [
      { src: `${BASE}/bi-step-4.png`, alt: 'Imported products in draft list', caption: 'Step 4 – Review imported products in your draft list' },
    ],
    step5: [
      { src: `${BASE}/bi-step-5.png`, alt: 'Bulk import troubleshooting view', caption: 'Step 5 – Troubleshoot common import issues' },
    ],
  };

  return (
    <div className="layout-wrapper landing">
      <SeoHead
        title="Bulk Product Import | CodeRom Help"
        description="Import hundreds of Amazon products to eBay in one click with CodeRom bulk import."
      />
      <HelpBreadcrumbSchema slug="bulk-import" name="Bulk product import" />
      <Navbar />

      <div className="help-article-page">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9} xl={8}>
              {/* Breadcrumb */}
              <div className="breadcrumb-section">
                <Link to="/help" className="breadcrumb-link">Help Center</Link>
                <span className="breadcrumb-separator">›</span>
                <span className="breadcrumb-current">Bulk product import</span>
              </div>

              {/* Article Header */}
              <div className="article-header">
                <h1>Bulk product import</h1>
                <p className="article-subtitle">
                  Import hundreds of Amazon products at once using a CSV file. Coderom will process
                  them automatically and create optimised eBay listings for each one.
                </p>
                <div className="article-meta">
                  <span className="read-time">📖 6 min read</span>
                  <span className="category">Product Import</span>
                </div>
              </div>

              {/* Table of Contents */}
              <div className="table-of-contents">
                <h3>📋 On this page</h3>
                <ul>
                  <li><a href="#step1">1. Prepare your CSV file</a></li>
                  <li><a href="#step2">2. Upload and import</a></li>
                  <li><a href="#step3">3. Monitor import progress</a></li>
                  <li><a href="#step4">4. Review imported products</a></li>
                  <li><a href="#step5">5. Troubleshooting common issues</a></li>
                </ul>
              </div>

              {/* Article Content */}
              <div className="article-content">

                {/* Step 1 */}
                <div id="step1" className="content-section">
                  <h2>Step 1: Prepare your CSV file</h2>
                  <p>
                    Create a spreadsheet with one Amazon ASIN per row. You can use Excel, Google Sheets,
                    or any text editor.
                  </p>
                  <p>
                    Your CSV file needs just one column — the Amazon ASIN. Here's an example:
                  </p>

                  <div className="csv-example-table">
                    <table>
                      <thead>
                        <tr>
                          <th>ASIN</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>B09V3KXJPB</td>
                        </tr>
                        <tr>
                          <td>B0BSHF7WHW</td>
                        </tr>
                        <tr>
                          <td>B07FZ8S74R</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <HelpScreenshot images={screenshots.step1} stepLabel="Step 1: Prepare your CSV file" />

                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Tip:</strong> You can find the ASIN on any Amazon product page — it's in
                      the URL or under "Product Information". It always starts with "B0" and is 10 characters long.
                    </div>
                  </div>
                </div>

                {/* Step 2 */}
                <div id="step2" className="content-section">
                  <h2>Step 2: Upload and import</h2>
                  <ol>
                    <li>Go to <strong>Bulk Import</strong> from the sidebar</li>
                    <li>Click <strong>Choose File</strong> and select your CSV</li>
                    <li>Choose your target eBay marketplace (GB, US, DE, etc.)</li>
                    <li>Toggle <strong>AI Optimise</strong> on if you want AI-generated titles and descriptions</li>
                    <li>Click <strong>Start Import</strong></li>
                  </ol>

                  <HelpScreenshot images={screenshots.step2} stepLabel="Step 2: Upload and import" />

                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Tip:</strong> You can import up to 500 ASINs at once. For larger lists,
                      split them into multiple CSV files.
                    </div>
                  </div>
                </div>

                {/* Step 3 */}
                <div id="step3" className="content-section">
                  <h2>Step 3: Monitor import progress</h2>
                  <p>
                    After clicking <strong>Start Import</strong>, you'll see a progress bar showing:
                  </p>
                  <ul>
                    <li>Total products in the queue</li>
                    <li>Successfully processed</li>
                    <li>Failed (with reasons)</li>
                  </ul>
                  <p>
                    The import runs in the background — you can close the page and come back later.
                    Coderom will continue processing.
                  </p>

                  <HelpScreenshot images={screenshots.step3} stepLabel="Step 3: Monitor import progress" />
                </div>

                {/* Step 4 */}
                <div id="step4" className="content-section">
                  <h2>Step 4: Review imported products</h2>
                  <p>
                    Once the import is complete:
                  </p>
                  <ol>
                    <li>Go to <strong>Draft Products</strong></li>
                    <li>You'll see all imported products with their status</li>
                    <li>Products marked "Ready" can be published to eBay immediately</li>
                    <li>Products marked "Needs Review" may need manual adjustments (e.g. missing images or restricted categories)</li>
                  </ol>

                  <HelpScreenshot images={screenshots.step4} stepLabel="Step 4: Review imported products" />
                </div>

                {/* Step 5 */}
                <div id="step5" className="content-section">
                  <h2>Step 5: Troubleshooting common issues</h2>

                  <div className="troubleshooting-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Issue</th>
                          <th>Cause</th>
                          <th>Solution</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>"ASIN not found"</strong></td>
                          <td>Invalid ASIN or product not available on Amazon</td>
                          <td>Double-check the ASIN on Amazon</td>
                        </tr>
                        <tr>
                          <td><strong>"Restricted category"</strong></td>
                          <td>Product is in a restricted eBay category</td>
                          <td>Remove the product or apply for category approval on eBay</td>
                        </tr>
                        <tr>
                          <td><strong>"No images available"</strong></td>
                          <td>Amazon listing has no images</td>
                          <td>Add images manually in the draft editor</td>
                        </tr>
                        <tr>
                          <td><strong>"Import stuck"</strong></td>
                          <td>Large batch processing</td>
                          <td>Wait a few minutes — processing continues in the background</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <HelpScreenshot images={screenshots.step5} stepLabel="Step 5: Troubleshooting common issues" />
                </div>

                {/* What's Next */}
                <div className="whats-next-section">
                  <h2>What's next?</h2>
                  <p>Now that you've imported products in bulk, explore these features:</p>
                  <div className="next-steps">
                    <Link to="/help/ai-listing" className="next-step-card">
                      <div className="next-step-icon">🤖</div>
                      <div className="next-step-content">
                        <h4>AI-powered product listing</h4>
                        <p>Let GPT-4o create optimised titles and descriptions automatically</p>
                      </div>
                    </Link>
                    <Link to="/help/templates" className="next-step-card">
                      <div className="next-step-icon">🎨</div>
                      <div className="next-step-content">
                        <h4>Professional listing templates</h4>
                        <p>Combine AI-generated content with custom templates for a branded look</p>
                      </div>
                    </Link>
                    <Link to="/help/pricing" className="next-step-card">
                      <div className="next-step-icon">💰</div>
                      <div className="next-step-content">
                        <h4>Smart repricing & pricing rules</h4>
                        <p>Automatically adjust prices based on Amazon changes and market conditions</p>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Was This Helpful */}
                <div className="helpful-section">
                  <h3>Was this helpful?</h3>
                  <div className="helpful-buttons">
                    <button className="helpful-btn helpful-yes">👍 Yes</button>
                    <button className="helpful-btn helpful-no">👎 No</button>
                  </div>
                  <p className="helpful-text">
                    Still need help? <Link to="/help">Browse all articles</Link> or{' '}
                    <a href="mailto:support@coderom.com">contact support</a>.
                  </p>
                </div>

              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <style>{`
        .help-article-page {
          padding: 120px 0 80px;
          background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
          min-height: 100vh;
        }

        /* Breadcrumb */
        .breadcrumb-section {
          margin-bottom: 24px;
          font-size: 14px;
        }
        .breadcrumb-link {
          color: #6366f1;
          text-decoration: none;
          transition: color 0.2s;
        }
        .breadcrumb-link:hover {
          color: #4f46e5;
        }
        .breadcrumb-separator {
          margin: 0 8px;
          color: #cbd5e1;
        }
        .breadcrumb-current {
          color: #64748b;
        }

        /* Article Header */
        .article-header {
          margin-bottom: 40px;
        }
        .article-header h1 {
          font-size: 42px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          line-height: 1.2;
        }
        .article-subtitle {
          font-size: 18px;
          color: #64748b;
          line-height: 1.6;
          margin-bottom: 20px;
        }
        .article-meta {
          display: flex;
          gap: 16px;
          align-items: center;
          font-size: 14px;
        }
        .read-time {
          color: #64748b;
        }
        .category {
          background: #f1f5f9;
          color: #6366f1;
          padding: 4px 12px;
          border-radius: 12px;
          font-weight: 500;
        }

        /* Table of Contents */
        .table-of-contents {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 40px;
        }
        .table-of-contents h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 16px;
        }
        .table-of-contents ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .table-of-contents li {
          margin-bottom: 8px;
        }
        .table-of-contents a {
          color: #6366f1;
          text-decoration: none;
          font-size: 15px;
          transition: color 0.2s;
        }
        .table-of-contents a:hover {
          color: #4f46e5;
          text-decoration: underline;
        }

        /* Article Content */
        .article-content {
          color: #334155;
          font-size: 16px;
          line-height: 1.8;
        }
        .content-section {
          margin-bottom: 48px;
          scroll-margin-top: 100px;
        }
        .content-section h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          margin-top: 40px;
        }
        .content-section h3 {
          font-size: 20px;
          font-weight: 600;
          color: #334155;
          margin-top: 24px;
          margin-bottom: 12px;
        }
        .content-section p {
          margin-bottom: 16px;
        }
        .content-section ul,
        .content-section ol {
          margin-bottom: 16px;
          padding-left: 24px;
        }
        .content-section li {
          margin-bottom: 8px;
        }
        .content-section strong {
          color: #1e293b;
          font-weight: 600;
        }

        /* Tip Box */
        .tip-box {
          display: flex;
          gap: 12px;
          background: #eff6ff;
          border-left: 4px solid #3b82f6;
          padding: 16px;
          border-radius: 8px;
          margin: 24px 0;
        }
        .tip-icon {
          font-size: 20px;
          flex-shrink: 0;
        }
        .tip-content {
          font-size: 15px;
          line-height: 1.6;
          color: #1e40af;
        }

        /* Tables */
        .csv-example-table,
        .troubleshooting-table {
          margin: 24px 0;
          overflow-x: auto;
        }
        .csv-example-table table,
        .troubleshooting-table table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .csv-example-table th,
        .troubleshooting-table th {
          background: #f8fafc;
          color: #1e293b;
          font-weight: 600;
          text-align: left;
          padding: 12px 16px;
          border-bottom: 2px solid #e2e8f0;
          font-size: 14px;
        }
        .csv-example-table td,
        .troubleshooting-table td {
          padding: 12px 16px;
          border-bottom: 1px solid #f1f5f9;
          font-size: 15px;
        }
        .csv-example-table tbody tr:last-child td,
        .troubleshooting-table tbody tr:last-child td {
          border-bottom: none;
        }
        .csv-example-table tbody tr:hover,
        .troubleshooting-table tbody tr:hover {
          background: #f8fafc;
        }

        /* What's Next Section */
        .whats-next-section {
          margin-top: 60px;
          padding-top: 40px;
          border-top: 2px solid #e2e8f0;
        }
        .whats-next-section h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
        }
        .next-steps {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-top: 24px;
        }
        .next-step-card {
          display: flex;
          gap: 16px;
          padding: 20px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          text-decoration: none;
          transition: all 0.3s;
        }
        .next-step-card:hover {
          border-color: #6366f1;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
          transform: translateY(-2px);
        }
        .next-step-icon {
          font-size: 32px;
          flex-shrink: 0;
        }
        .next-step-content h4 {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 4px;
        }
        .next-step-content p {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }

        /* Was This Helpful */
        .helpful-section {
          margin-top: 60px;
          padding: 32px;
          background: #f8fafc;
          border-radius: 12px;
          text-align: center;
        }
        .helpful-section h3 {
          font-size: 20px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 20px;
        }
        .helpful-buttons {
          display: flex;
          gap: 12px;
          justify-content: center;
          margin-bottom: 16px;
        }
        .helpful-btn {
          padding: 10px 24px;
          border: 2px solid #e2e8f0;
          background: white;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        .helpful-btn:hover {
          border-color: #6366f1;
          background: #eff6ff;
        }
        .helpful-text {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }
        .helpful-text a {
          color: #6366f1;
          text-decoration: none;
        }
        .helpful-text a:hover {
          text-decoration: underline;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .help-article-page {
            padding: 100px 0 60px;
          }
          .article-header h1 {
            font-size: 32px;
          }
          .article-subtitle {
            font-size: 16px;
          }
          .content-section h2 {
            font-size: 24px;
          }
          .csv-example-table,
          .troubleshooting-table {
            font-size: 14px;
          }
          .next-step-card {
            flex-direction: column;
            text-align: center;
          }
        }
      `}</style>

      <Footer />
      <GuestChatWidget />
    </div>
  );
};

export default HelpArticleBulkImport;
